import { Outlet } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import type { FeatureKey } from '@hapana/shared/schemas/entitlements/feature-key.schema'
import { FEATURE_LABEL_KEYS } from '@hapana/shared/constants/entitlements.constants'
import type { UseEntitlementsReturn } from '../../../hooks/entitlements.hooks'
import { FeatureDisabled } from './feature-disabled.component'

export interface UpgradeRequest {
  feature: FeatureKey
  /** The feature's display name, e.g. "Developer API". */
  featureName: string
  /** What unlocks it, e.g. "Available on the Premium plan." Empty when unknown. */
  upgradeMessage: string
}

interface RequireFeatureRouteProps {
  /** The feature key(s) to check */
  feature: FeatureKey | FeatureKey[]
  /** The useEntitlements hook return — call useEntitlements() in parent and pass here */
  entitlements: UseEntitlementsReturn
  /** Called when the user clicks upgrade, with what they were blocked from (to prefill a request). */
  onUpgrade?: (request: UpgradeRequest) => void
  /** If true, requires ALL features. If false (default), requires ANY. */
  requireAll?: boolean
}

/**
 * Route-level feature gate. Wraps routes that require specific feature(s).
 *
 * If entitled → renders <Outlet /> (child routes).
 * If not → renders <FeatureDisabled> with the appropriate message.
 *
 * Usage: Create a wrapper component in your app that calls useEntitlements()
 * and passes the result to this component:
 *
 * ```jsx
 * const FeatureGatedRoute = ({ feature }) => {
 *   const { siteId } = useParams()
 *   const entitlements = useEntitlements(siteId)
 *   return <RequireFeatureRoute feature={feature} entitlements={entitlements} />
 * }
 *
 * // In router:
 * <Route element={<FeatureGatedRoute feature={FeatureKeys.BiDashboards} />}>
 *   <Route path="analytics" element={<Analytics />} />
 * </Route>
 * ```
 */
const RequireFeatureRoute = ({
  feature,
  entitlements,
  onUpgrade,
  requireAll = false,
}: RequireFeatureRouteProps) => {
  const { t } = useTranslation('common')
  const { hasFeature, checkFeature, isLoading } = entitlements

  if (isLoading) {
    return null
  }

  const features = Array.isArray(feature) ? feature : [feature]
  const isEntitled = requireAll
    ? features.every((f) => hasFeature(f))
    : features.some((f) => hasFeature(f))

  // The gate names the first non-entitled feature. Nothing blocked (which includes being handed an
  // empty list) means there is nothing to gate on, so the child routes render.
  const blockedFeature = features.find((f) => !hasFeature(f))

  if (isEntitled || !blockedFeature) {
    return <Outlet />
  }

  const checkResult = checkFeature(blockedFeature)
  const featureName = t(FEATURE_LABEL_KEYS[blockedFeature])

  return (
    <FeatureDisabled
      checkResult={checkResult}
      featureName={featureName}
      onUpgrade={
        onUpgrade
          ? () =>
              onUpgrade({
                feature: blockedFeature,
                featureName,
                upgradeMessage: checkResult.upgradeInfo?.message ?? '',
              })
          : undefined
      }
    />
  )
}

export default RequireFeatureRoute
